import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  BackgroundVariant,
  MarkerType,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useCanvasFocus, selectOnlyChanges }  from '../lib/useGraphFocus'
import { useRememberedViewport } from '../lib/useGraphViewport'
import { StateNode }       from './nodes/StateNode'
import { EntryNode }       from './nodes/EntryNode'
import { TransitionEdge }  from './nodes/TransitionEdge'
import { PlayClipNode }    from './nodes/PlayClipNode'
import { BlendNode }       from './nodes/BlendNode'
import { RandomNode }      from './nodes/RandomNode'
import { SelectNode }      from './nodes/SelectNode'
import { StateOutputNode } from './nodes/StateOutputNode'
import { initSM, getCurrentState, smRuntime } from '../lib/smRuntime'
import { debugState } from '../lib/debugState'
import { getSmSplineStyle, setSmSplineStyle, SPLINE_STYLES } from '../lib/smSplineStyle'
import { SM_THEME as T } from './nodes/stateMachineTheme'
import AnchoredOverlay from './AnchoredOverlay'

const SM_NODE_TYPES = {
  state: StateNode,
  entry: EntryNode,
}

const SM_EDGE_TYPES = {
  transition: TransitionEdge,
}

const STATE_INNER_NODE_TYPES = {
  playClip:    PlayClipNode,
  blend:       BlendNode,
  random:      RandomNode,
  select:      SelectNode,
  stateOutput: StateOutputNode,
}

const DEFAULT_STATE_INNER_NODES = () => [{
  id: 'output',
  type: 'stateOutput',
  position: { x: 350, y: 120 },
  data: {},
  deletable: false,
}]

let _stateId = 1
const nextStateId = () => `state_${_stateId++}`

function normalizeSMEdges(edges) {
  return (edges ?? []).map(e => {
    if (e.sourceHandle === '-' || !e.sourceHandle) {
      return {
        ...e,
        sourceHandle: 'out',
        targetHandle: 'in',
        type: 'transition',
        markerEnd: e.markerEnd ?? { type: MarkerType.ArrowClosed, color: T.marker },
        data: e.data ?? {},
      }
    }
    return e
  })
}

// ── Helper: find the first PlayClip's clipName inside a state inner graph ──
function getStateClipName(stateNode) {
  const sEdges = stateNode?.data?.innerEdges ?? []
  const sNodes = stateNode?.data?.innerNodes ?? []
  const outEdge = sEdges.find(e => e.target === 'output')
  if (!outEdge) return null
  const src = sNodes.find(n => n.id === outEdge.source)
  if (src?.type === 'playClip') return src.data?.clipName ?? null
  return null
}

// ─── State inner canvas ───────────────────────────────────────────────────────
function StateInnerCanvas({ nodes, edges, onNodesChange, onEdgesChange, onConnect, onAdd }) {
  const { screenToFlowPosition } = useReactFlow()
  const [contextMenu, setContextMenu] = useState(null)

  const onPaneContextMenu = useCallback((e) => {
    e.preventDefault()
    const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY })
    setContextMenu({ screenX: e.clientX, screenY: e.clientY, flowPos })
  }, [screenToFlowPosition])

  const closeMenu = useCallback(() => setContextMenu(null), [])

  useEffect(() => {
    if (!contextMenu) return
    const h = () => closeMenu()
    window.addEventListener('mousedown', h)
    return () => window.removeEventListener('mousedown', h)
  }, [contextMenu, closeMenu])

  const menuItem = (label, type) => (
    <div
      key={type}
      onClick={() => { onAdd(type, contextMenu.flowPos); closeMenu() }}
      style={{
        padding: '7px 12px',
        cursor: 'pointer',
        color: T.btnText,
        fontSize: 11,
        borderLeft: `3px solid ${T.btnBorder}`,
        margin: '3px 6px',
        borderRadius: '0 4px 4px 0',
      }}
      onMouseEnter={e => e.currentTarget.style.background = '#1f1f1f'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      {label}
    </div>
  )

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onPaneContextMenu={onPaneContextMenu}
        onPaneClick={closeMenu}
        nodeTypes={STATE_INNER_NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        deleteKeyCode="Delete"
        style={{ background: T.canvasBg }}
      >
        <Background variant={BackgroundVariant.Dots} color={T.dots} gap={20} size={1} />
        <Controls style={{ background: T.btnBg, border: `1px solid ${T.btnBorder}`, color: T.btnText }} />
      </ReactFlow>

      {contextMenu && (
        <AnchoredOverlay
          x={contextMenu.screenX} y={contextMenu.screenY}
          style={{
            background: '#141414',
            border: '1px solid #303030',
            borderRadius: 6,
            boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
            zIndex: 1000,
            overflow: 'hidden',
            fontFamily: 'var(--m-font)',
            minWidth: 140,
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          <div style={{ padding: '4px 0' }}>
            {menuItem('+ Play Clip', 'playClip')}
            {menuItem('+ Blend',     'blend')}
            {menuItem('+ Random',    'random')}
            {menuItem('+ Select',    'select')}
          </div>
        </AnchoredOverlay>
      )}
    </>
  )
}

// ─── StateInnerEditor ─────────────────────────────────────────────────────────
function StateInnerEditor({ stateId, stateName, smName, initialNodes, initialEdges, graphType, onBack, onGraphChange }) {
  const initNodes = (initialNodes?.length > 0 ? initialNodes : DEFAULT_STATE_INNER_NODES())
  const [nodes, setNodes, onNodesChange] = useNodesState(initNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges ?? [])

  useEffect(() => {
    onGraphChange?.(nodes, edges)
  }, [nodes, edges]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateNode = useCallback((nodeId, key, value) => {
    setNodes(ns => ns.map(n => n.id === nodeId ? { ...n, data: { ...n.data, [key]: value } } : n))
  }, [setNodes])

  const nodesWithHandlers = nodes.map(n => ({
    ...n,
    data: {
      ...n.data,
      _graphType: graphType,
      connected: n.id === 'output' ? edges.some(e => e.target === 'output') : n.data.connected,
      onChange: n.type !== 'stateOutput' ? (key, val) => updateNode(n.id, key, val) : undefined,
    },
  }))

  const onConnect = useCallback((params) => {
    setEdges(es => {
      const filtered = es.filter(e => !(e.target === params.target && e.targetHandle === params.targetHandle))
      return addEdge({ ...params }, filtered)
    })
  }, [setEdges])

  const addNode = useCallback((type, position) => {
    const id = `sn_${Date.now()}`
    const pos = position ?? { x: 150, y: 100 }
    if (type === 'playClip') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { clipName: '', playSpeed: 1.0 } }])
    } else if (type === 'blend') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { alpha: 0.5 } }])
    } else if (type === 'random') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { randomType: 'int', min: 0, max: 1 } }])
    } else if (type === 'select') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { selectType: 'animation', optionCount: 2, index: 0 } }])
    }
  }, [setNodes])

  return (
    <div style={{ width: '100%', height: '100%', background: T.canvasBg, position: 'relative' }}>

      {/* Breadcrumb */}
      <div style={{
        position: 'absolute', top: 10, left: 10, zIndex: 10,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <button
          onClick={onBack}
          style={{
            background: T.backBg, border: `1px solid ${T.backBorder}`, color: T.backText,
            borderRadius: 4, padding: '5px 12px', fontSize: 11,
            fontFamily: 'var(--m-font)', cursor: 'pointer',
          }}
        >
          ← Back
        </button>
        <span style={{ color: T.breadcrumbFaint, fontSize: 11, fontFamily: 'var(--m-font)' }}>
          {smName} /
        </span>
        <span style={{ color: T.breadcrumbName, fontSize: 11, fontFamily: 'var(--m-font)', fontWeight: 'bold' }}>
          {stateName}
        </span>
      </div>

      <div style={{
        position: 'absolute', top: 10, left: 200, zIndex: 10,
        display: 'flex', alignItems: 'center',
      }}>
        <span style={{ color: T.hint, fontSize: 10, fontFamily: 'var(--m-font)' }}>
          right-click to add nodes
        </span>
      </div>

      <ReactFlowProvider>
        <StateInnerCanvas
          nodes={nodesWithHandlers}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onAdd={addNode}
        />
      </ReactFlowProvider>
    </div>
  )
}

// ─── SM flow canvas ───────────────────────────────────────────────────────────
function SMFlowCanvas({ nodes, edges, onNodesChange, onEdgesChange, onConnect, onAddState, onEdgeMouseEnter, onEdgeMouseLeave, graphType = 'body', smNodeId = null }) {
  const { screenToFlowPosition } = useReactFlow()
  const [contextMenu, setContextMenu] = useState(null)
  // Serves a Ctrl+F / agent focus request for a state INSIDE this machine —
  // the parent editor drills in, this centres on it.
  useCanvasFocus({
    graph: graphType,
    containerId: smNodeId,
    onSelectNode: useCallback(
      (id) => onNodesChange(selectOnlyChanges(nodes, id)),
      [onNodesChange, nodes],
    ),
  })
  // A state machine is a canvas you close and reopen like any other.
  const viewportProps = useRememberedViewport({ surface: graphType, containerId: smNodeId })

  const onPaneContextMenu = useCallback((e) => {
    e.preventDefault()
    const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY })
    setContextMenu({ screenX: e.clientX, screenY: e.clientY, flowPos })
  }, [screenToFlowPosition])

  const closeMenu = useCallback(() => setContextMenu(null), [])

  useEffect(() => {
    if (!contextMenu) return
    const h = () => closeMenu()
    window.addEventListener('mousedown', h)
    return () => window.removeEventListener('mousedown', h)
  }, [contextMenu, closeMenu])

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onEdgeMouseEnter={onEdgeMouseEnter}
        onEdgeMouseLeave={onEdgeMouseLeave}
        onPaneContextMenu={onPaneContextMenu}
        onPaneClick={closeMenu}
        nodeTypes={SM_NODE_TYPES}
        edgeTypes={SM_EDGE_TYPES}
        {...viewportProps}
        fitViewOptions={{ padding: 0.3 }}
        deleteKeyCode="Delete"
        style={{ background: T.canvasBg }}
      >
        <Background variant={BackgroundVariant.Dots} color={T.dots} gap={20} size={1} />
        <Controls style={{ background: T.btnBg, border: `1px solid ${T.btnBorder}`, color: T.btnText }} />
      </ReactFlow>

      {contextMenu && (
        <AnchoredOverlay
          x={contextMenu.screenX} y={contextMenu.screenY}
          style={{
            background: '#141414',
            border: '1px solid #303030',
            borderRadius: 6,
            boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
            zIndex: 1000,
            overflow: 'hidden',
            fontFamily: 'var(--m-font)',
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          <div
            onClick={() => { onAddState(contextMenu.flowPos); closeMenu() }}
            style={{
              padding: '8px 14px', cursor: 'pointer', color: T.btnText, fontSize: 12,
              borderLeft: `3px solid ${T.btnBorder}`, margin: '4px 6px', borderRadius: '0 4px 4px 0',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#1f1f1f'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            + State
          </div>
        </AnchoredOverlay>
      )}
    </>
  )
}

// ─── StateMachineEditor ───────────────────────────────────────────────────────
export function StateMachineEditor({
  smNodeId,
  smName,
  initialNodes = [],
  initialEdges = [],
  graphType = 'body',
  onBack,
  onGraphChange,
  onOpenRule,
  ruleResults = {},
}) {
  const [innerNodes, setInnerNodes, onInnerNodesChange] = useNodesState(initialNodes)
  const [innerEdges, setInnerEdges, onInnerEdgesChange] = useEdgesState(normalizeSMEdges(initialEdges))

  // SM-level view stack: sm | state
  const [viewStack, setViewStack] = useState([{ type: 'sm' }])
  const currentView = viewStack[viewStack.length - 1]
  const pushView = (v) => setViewStack(s => [...s, v])
  const popView  = ()  => setViewStack(s => s.slice(0, -1))

  const [activeStateId, setActiveStateId] = useState(() => getCurrentState(smNodeId))
  const activeStateRef = useRef(activeStateId)
  const [clipTimings, setClipTimings] = useState({})

  // ENG-1061: transition spline shape (view preference, persisted per-user).
  const [splineStyle, setSplineStyle] = useState(getSmSplineStyle)
  const changeSplineStyle = useCallback((v) => {
    setSplineStyle(setSmSplineStyle(v))
  }, [])

  // ENG-1061: hover-to-trace — hovering one transition (its line or its rule
  // badge) spotlights it and fades the rest, so a single connection is legible
  // even where many overlap. Plain UI state (not per-frame), fires on enter/leave.
  const [hoveredEdgeId, setHoveredEdgeId] = useState(null)
  const onEdgeEnter = useCallback((_e, edge) => setHoveredEdgeId(edge.id), [])
  const onEdgeLeave = useCallback(() => setHoveredEdgeId(null), [])

  // ENG-1061: persist a transition's reroute waypoints (Unreal-style). Writes
  // edge.data.waypoints; the existing onGraphChange effect auto-saves it.
  const setEdgeWaypoints = useCallback((edgeId, waypoints) => {
    setInnerEdges(es => es.map(e =>
      e.id === edgeId ? { ...e, data: { ...e.data, waypoints } } : e
    ))
  }, [setInnerEdges])

  useEffect(() => {
    onGraphChange?.(innerNodes, innerEdges)
  }, [innerNodes, innerEdges]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    initSM(smNodeId)
  }, [smNodeId])

  useEffect(() => {
    const id = setInterval(() => {
      const current = smRuntime[smNodeId]?.currentState ?? null
      if (current !== activeStateRef.current) {
        activeStateRef.current = current
        setActiveStateId(current)
      }
      setClipTimings({ ...debugState.clipTimings })
    }, 100)
    return () => clearInterval(id)
  }, [smNodeId])

  const updateInnerNode = useCallback((nodeId, key, value) => {
    setInnerNodes(ns => ns.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, [key]: value } } : n
    ))
  }, [setInnerNodes])

  const updateStateInnerGraph = useCallback((stateId, sNodes, sEdges) => {
    setInnerNodes(ns => ns.map(n =>
      n.id === stateId ? { ...n, data: { ...n.data, innerNodes: sNodes, innerEdges: sEdges } } : n
    ))
  }, [setInnerNodes])

  const nodesWithHandlers = innerNodes.map(n => {
    const clipName = n.type === 'state' ? getStateClipName(n) : null
    return {
      ...n,
      data: {
        ...n.data,
        isActive:        n.id === activeStateId,
        _debugAnimRatio: clipName ? (clipTimings[clipName]?.ratio ?? null) : null,
        onChange: (key, value) => updateInnerNode(n.id, key, value),
        onDrillIn: n.type === 'state'
          ? () => pushView({ type: 'state', stateId: n.id, stateName: n.data?.name ?? 'State' })
          : undefined,
      },
    }
  })

  const edgesWithHandlers = innerEdges.map(e => {
    const hasBidirectional = e.source !== 'entry' && innerEdges.some(
      other => other.source === e.target && other.target === e.source
    )
    const labelOffset = hasBidirectional ? (e.source < e.target ? -22 : 22) : 0
    const hovered = e.id === hoveredEdgeId
    return {
      ...e,
      // NOTE: deliberately NO per-edge zIndex lift on hover. Raising it makes
      // ReactFlow render the hovered edge's SVG (including the transparent fat
      // "double-click to add a waypoint" hit-path) in a layer ABOVE the HTML
      // waypoint handles, so you couldn't grab a dot while hovering its edge
      // (ENG-1061 review). Hover-to-trace still reads via brightness/opacity.
      data: {
        ...e.data,
        onOpenRule:  (edgeId) => onOpenRule?.(edgeId),
        isActive:    e.target === activeStateId,
        hasRule:     !!(e.data?.ruleGraph?.nodes?.length > 0),
        actionCount: e.data?.actions?.length ?? 0,   // ENG-1062
        ruleResult:  ruleResults[`${smNodeId}::${e.id}`] ?? null,
        isEntryEdge: e.source === 'entry',
        splineStyle,                                  // ENG-1061
        hovered,                                      // ENG-1061 hover-to-trace
        anyHovered:  hoveredEdgeId !== null,
        onHover:     setHoveredEdgeId,
        onHoverEnd:  onEdgeLeave,
        onWaypointsChange: setEdgeWaypoints,          // ENG-1061 reroute waypoints
        labelOffset,
      },
    }
  })

  const onConnect = useCallback((params) => {
    setInnerEdges(es => {
      let filtered = es
      if (params.source === 'entry') {
        filtered = es.filter(e => e.source !== 'entry')
      }
      return addEdge({
        ...params,
        type: 'transition',
        markerEnd: { type: MarkerType.ArrowClosed, color: T.marker },
        data: {},
      }, filtered)
    })
  }, [setInnerEdges])

  const addState = useCallback((position) => {
    const id = nextStateId()
    setInnerNodes(ns => [...ns, {
      id,
      type: 'state',
      position: position ?? { x: 200 + Math.random() * 200, y: 80 + Math.random() * 200 },
      data: {
        name: 'New State',
        innerNodes: DEFAULT_STATE_INNER_NODES(),
        innerEdges: [],
      },
    }])
  }, [setInnerNodes])

  // Current state node for the state-inner view
  const currentStateNode = currentView.type === 'state'
    ? innerNodes.find(n => n.id === currentView.stateId)
    : null

  return (
    <div style={{ width: '100%', height: '100%', background: T.canvasBg, position: 'relative' }}>

      {/* ── SM view ── */}
      {currentView.type === 'sm' && (
        <>
          <div style={{
            position: 'absolute', top: 10, left: 10, zIndex: 10,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <button
              onClick={onBack}
              style={{
                background: T.backBg, border: `1px solid ${T.backBorder}`, color: T.backText,
                borderRadius: 4, padding: '5px 12px', fontSize: 11,
                fontFamily: 'var(--m-font)', cursor: 'pointer',
              }}
            >
              ← Back
            </button>
            <span style={{ color: T.breadcrumbFaint, fontSize: 11, fontFamily: 'var(--m-font)' }}>Blueprint /</span>
            <span style={{ color: T.breadcrumbName, fontSize: 11, fontFamily: 'var(--m-font)', fontWeight: 'bold' }}>
              {smName ?? 'State Machine'}
            </span>
          </div>

          <div style={{
            position: 'absolute', top: 10, left: 160, zIndex: 10,
            display: 'flex', gap: 8, alignItems: 'center',
          }}>
            <button
              onClick={() => addState()}
              style={{
                background: T.btnBg, border: `1px solid ${T.btnBorder}`, color: T.btnText,
                borderRadius: 4, padding: '5px 12px', fontSize: 11,
                fontFamily: 'var(--m-font)', cursor: 'pointer',
              }}
            >
              + State
            </button>
            <span style={{ color: T.hint, fontSize: 10, fontFamily: 'var(--m-font)' }}>
              double-click state to edit · right-click to add · double-click a line to add a reroute point
            </span>

            {/* ENG-1061: transition spline shape — themed (tokens only) */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8,
              fontFamily: 'var(--m-font)',
            }}>
              <span style={{ color: 'var(--m-fg-3)', fontSize: 10 }}>Splines</span>
              <div style={{
                display: 'flex',
                border: '1px solid var(--m-line)',
                borderRadius: 'var(--m-radius-sm)',
                overflow: 'hidden',
              }}>
                {SPLINE_STYLES.map(s => {
                  const on = splineStyle === s.value
                  return (
                    <button
                      key={s.value}
                      onClick={() => changeSplineStyle(s.value)}
                      title={s.title}
                      style={{
                        background: on ? 'var(--m-accent)' : 'var(--m-panel)',
                        color: on ? 'var(--m-accent-ink)' : 'var(--m-fg-3)',
                        border: 'none',
                        padding: '5px 10px',
                        fontSize: 10,
                        fontFamily: 'var(--m-font)',
                        cursor: 'pointer',
                      }}
                    >
                      {s.label}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          <ReactFlowProvider>
            <SMFlowCanvas
              nodes={nodesWithHandlers}
              edges={edgesWithHandlers}
              onNodesChange={onInnerNodesChange}
              onEdgesChange={onInnerEdgesChange}
              onConnect={onConnect}
              onAddState={addState}
              onEdgeMouseEnter={onEdgeEnter}
              onEdgeMouseLeave={onEdgeLeave}
              graphType={graphType}
              smNodeId={smNodeId}
            />
          </ReactFlowProvider>
        </>
      )}

      {/* ── State inner graph view ── */}
      {currentView.type === 'state' && currentStateNode && (
        <StateInnerEditor
          key={currentView.stateId}
          stateId={currentView.stateId}
          stateName={currentView.stateName}
          smName={smName ?? 'State Machine'}
          initialNodes={currentStateNode.data?.innerNodes}
          initialEdges={currentStateNode.data?.innerEdges}
          graphType={graphType}
          onBack={popView}
          onGraphChange={(sNodes, sEdges) => updateStateInnerGraph(currentView.stateId, sNodes, sEdges)}
        />
      )}
    </div>
  )
}
